import React, { useState } from 'react'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import {
  Box,
  Typography,
  Button,
  Divider,
  Tooltip,
  FormControlLabel,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
} from '@mui/material'
import { formatDate } from '../utils/documentUtils.js'

const DocumentsHeader = ({
  selectedFirm,
  requestTask,
  documents,
  onFinishTask,
  unmatchedFiles = [],
  isSaving,
}) => {
  const isFinishButtonDisabled =
    !documents.flatMap(doc => doc.files || []).length && !unmatchedFiles.length

  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const handleOpenModal = () => {
    setIsConfirmed(false)
    setOpenConfirmModal(true)
  }

  const handleCloseModal = () => {
    setOpenConfirmModal(false)
    setIsConfirmed(false)
  }

  const handleConfirmFinish = () => {
    onFinishTask()
    handleCloseModal()
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="bold"
          >
            {selectedFirm.name} is requesting files
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
          >
            for {requestTask.description}
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            <strong>Requested:</strong> {formatDate(requestTask.requestDate)} |{' '}
            <strong>Due:</strong> {formatDate(requestTask.dueDate)}
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Button
            variant="contained"
            color="primary"
            disabled={isFinishButtonDisabled}
            onClick={handleOpenModal}
          >
            I'm Finished, Notify Requesting Team
          </Button>
          {!isFinishButtonDisabled && (
            <Tooltip
              title="All changes are automatically saved. You can leave this page and return later to upload more files. When you're completely finished, click 'I'm Finished, Notify Requesting Team' to send a final notification. Note: After clicking, you will no longer be able to access this request link."
              arrow
            >
              <Box
                mt={1}
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ cursor: 'pointer', gap: '8px' }}
              >
                <CloudDoneOutlinedIcon
                  sx={{
                    fontSize: 24,
                  }}
                />

                <Typography
                  component="span"
                  fontWeight="bold"
                  fontSize="0.9rem"
                  sx={{
                    marginTop: '2px',
                  }}
                >
                  {isSaving ? 'Saving...' : 'All changes saved'}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Divider />
      <Dialog
        open={openConfirmModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            padding: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', pb: 2 }}>
          Confirm Submission
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ py: 3 }}
        >
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.6 }}
          >
            I have completed uploading my files and the link to access this
            portal will deactivate.
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            My accountant will be notified that I have completed uploading
            files.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={e => setIsConfirmed(e.target.checked)}
              />
            }
            label="I understand and wish to proceed."
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseModal}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isConfirmed}
            onClick={handleConfirmFinish}
            size="large"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DocumentsHeader
